export type AdminV2ArrayDirection = -1 | 1;

export const ADMIN_V2_STABLE_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,80}$/;

export function isAdminV2StableId(value: string) {
  return ADMIN_V2_STABLE_ID_PATTERN.test(value);
}

let adminV2StableLocalIdSequence = 0;

function normalizeAdminV2StableLocalIdPrefix(prefix: string) {
  const normalized = prefix
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^[^a-zA-Z0-9]+/, '')
    .replace(/[-_]+$/, '')
    .slice(0, 32);

  if (!normalized || !isAdminV2StableId(normalized)) {
    throw new Error('Admin v2 stable local id prefix is invalid.');
  }

  return normalized;
}

function normalizeAdminV2StableLocalIdEntropy(value: string) {
  const normalized = value.replace(/[^a-zA-Z0-9_-]+/g, '').slice(0, 20);
  if (!normalized) {
    throw new Error('Admin v2 stable local id entropy is invalid.');
  }
  return normalized;
}

function createDefaultAdminV2StableLocalIdEntropy() {
  const randomUUID = globalThis.crypto?.randomUUID;
  if (typeof randomUUID !== 'function') {
    throw new Error(
      'Admin v2 stable local id generation requires crypto.randomUUID.'
    );
  }
  return randomUUID.call(globalThis.crypto);
}

export function createAdminV2StableLocalId(params: {
  prefix: string;
  existingIds?: readonly string[];
  entropy?: () => string;
}) {
  const prefix = normalizeAdminV2StableLocalIdPrefix(params.prefix);
  const entropy = params.entropy ?? createDefaultAdminV2StableLocalIdEntropy;
  const existingIds = new Set(params.existingIds ?? []);

  for (let attempt = 0; attempt < 10; attempt += 1) {
    adminV2StableLocalIdSequence += 1;
    const suffix = normalizeAdminV2StableLocalIdEntropy(entropy());
    const candidate = `${prefix}-${adminV2StableLocalIdSequence.toString(
      36
    )}-${suffix}`;
    if (isAdminV2StableId(candidate) && !existingIds.has(candidate)) {
      return candidate;
    }
  }

  throw new Error('Unable to allocate a stable admin v2 local id.');
}

export function getAdminV2SortedAllowedItemCounts(
  allowedItemCounts: readonly number[] = []
) {
  return [...allowedItemCounts].sort((left, right) => left - right);
}

export function getAdminV2NextArrayTargetCount(params: {
  currentCount: number;
  minItems?: number;
  maxItems?: number;
  allowedItemCounts?: readonly number[];
}) {
  const sortedAllowedCounts = getAdminV2SortedAllowedItemCounts(
    params.allowedItemCounts
  );
  if (sortedAllowedCounts.length > 0) {
    return (
      sortedAllowedCounts.find(
        (count) =>
          count > params.currentCount &&
          isAdminV2ArrayCountAllowed({
            count,
            minItems: params.minItems,
            maxItems: params.maxItems,
            allowedItemCounts: params.allowedItemCounts,
          })
      ) ?? null
    );
  }
  if (params.maxItems !== undefined && params.currentCount >= params.maxItems) {
    return null;
  }
  return params.currentCount + 1;
}

export function getAdminV2PreviousArrayTargetCount(params: {
  currentCount: number;
  minItems?: number;
  maxItems?: number;
  allowedItemCounts?: readonly number[];
}) {
  const sortedAllowedCounts = getAdminV2SortedAllowedItemCounts(
    params.allowedItemCounts
  );
  if (sortedAllowedCounts.length > 0) {
    const previousCounts = sortedAllowedCounts
      .filter((count) => count < params.currentCount)
      .filter((count) =>
        isAdminV2ArrayCountAllowed({
          count,
          minItems: params.minItems,
          maxItems: params.maxItems,
          allowedItemCounts: params.allowedItemCounts,
        })
      );
    return previousCounts[previousCounts.length - 1] ?? null;
  }
  const nextCount = params.currentCount - 1;
  return isAdminV2ArrayCountAllowed({
    count: nextCount,
    minItems: params.minItems,
    maxItems: params.maxItems,
    allowedItemCounts: params.allowedItemCounts,
  })
    ? nextCount
    : null;
}

export function isAdminV2ArrayCountAllowed(params: {
  count: number;
  minItems?: number;
  maxItems?: number;
  allowedItemCounts?: readonly number[];
}) {
  if (params.count < (params.minItems ?? 0)) {
    return false;
  }
  if (params.maxItems !== undefined && params.count > params.maxItems) {
    return false;
  }
  const sortedAllowedCounts = getAdminV2SortedAllowedItemCounts(
    params.allowedItemCounts
  );
  return (
    sortedAllowedCounts.length === 0 ||
    sortedAllowedCounts.includes(params.count)
  );
}

export function updateAdminV2ArrayItem<TItem>(params: {
  items: readonly TItem[];
  index: number;
  item: TItem;
}) {
  return params.items.map((current, currentIndex) =>
    currentIndex === params.index ? params.item : current
  );
}

export function removeAdminV2ArrayItem<TItem>(params: {
  items: readonly TItem[];
  index: number;
}) {
  return params.items.filter(
    (_, currentIndex) => currentIndex !== params.index
  );
}

export function getAdminV2ArrayRemovalIndices(params: {
  itemCount: number;
  index: number;
  targetCount: number;
}) {
  if (
    params.index < 0 ||
    params.index >= params.itemCount ||
    params.targetCount < 0 ||
    params.targetCount >= params.itemCount
  ) {
    return [];
  }
  const removeCount = params.itemCount - params.targetCount;
  const indices = new Set<number>([params.index]);
  for (
    let currentIndex = params.itemCount - 1;
    indices.size < removeCount && currentIndex >= 0;
    currentIndex -= 1
  ) {
    if (currentIndex !== params.index) {
      indices.add(currentIndex);
    }
  }
  return [...indices].sort((left, right) => left - right);
}

export function removeAdminV2ArrayItemsToTargetCount<TItem>(params: {
  items: readonly TItem[];
  index: number;
  targetCount: number;
}) {
  const removalIndices = new Set(
    getAdminV2ArrayRemovalIndices({
      itemCount: params.items.length,
      index: params.index,
      targetCount: params.targetCount,
    })
  );
  if (removalIndices.size === 0) {
    return [...params.items];
  }
  return params.items.filter(
    (_, currentIndex) => !removalIndices.has(currentIndex)
  );
}

export function moveAdminV2ArrayItem<TItem>(params: {
  items: readonly TItem[];
  index: number;
  direction: AdminV2ArrayDirection;
}) {
  const targetIndex = params.index + params.direction;
  if (targetIndex < 0 || targetIndex >= params.items.length) {
    return [...params.items];
  }
  const next = [...params.items];
  const current = next[params.index];
  next[params.index] = next[targetIndex];
  next[targetIndex] = current;
  return next;
}

export function moveAdminV2ArrayItemToIndex<TItem>(params: {
  items: readonly TItem[];
  fromIndex: number;
  toIndex: number;
}) {
  if (
    params.fromIndex < 0 ||
    params.fromIndex >= params.items.length ||
    params.toIndex < 0 ||
    params.toIndex >= params.items.length ||
    params.fromIndex === params.toIndex
  ) {
    return [...params.items];
  }
  const next = [...params.items];
  const [current] = next.splice(params.fromIndex, 1);
  next.splice(params.toIndex, 0, current);
  return next;
}

export function createAdminV2NextStableItemId(params: {
  existingItemIds: readonly string[];
  prefix?: string;
}) {
  const prefix = params.prefix ?? 'item';
  const existingItemIds = Array.from(new Set(params.existingItemIds));
  for (let index = existingItemIds.length + 1; index < 1000; index += 1) {
    const candidate = `${prefix}-${index}`;
    if (!existingItemIds.includes(candidate)) {
      return candidate;
    }
  }
  throw new Error('Unable to allocate a stable admin v2 item id.');
}

export function createAdminV2NextStableItemIds(params: {
  existingItemIds: readonly string[];
  count: number;
  prefix?: string;
}) {
  const nextIds = [...params.existingItemIds];
  for (let index = 0; index < params.count; index += 1) {
    nextIds.push(
      createAdminV2NextStableItemId({
        existingItemIds: nextIds,
        prefix: params.prefix,
      })
    );
  }
  return nextIds.slice(params.existingItemIds.length);
}

export function createAdminV2ArrayOperationController<TItem>(params: {
  items: readonly TItem[];
  createItem: () => TItem;
  onChange: (items: TItem[]) => void;
  readOnly?: boolean;
  minItems?: number;
  maxItems?: number;
  allowedItemCounts?: readonly number[];
}) {
  const targetCount = getAdminV2NextArrayTargetCount({
    currentCount: params.items.length,
    minItems: params.minItems,
    maxItems: params.maxItems,
    allowedItemCounts: params.allowedItemCounts,
  });
  const canAdd = !params.readOnly && targetCount !== null;
  const removeTargetCount = getAdminV2PreviousArrayTargetCount({
    currentCount: params.items.length,
    minItems: params.minItems,
    maxItems: params.maxItems,
    allowedItemCounts: params.allowedItemCounts,
  });
  const canRemoveItem = !params.readOnly && removeTargetCount !== null;

  return {
    targetCount,
    removeTargetCount,
    canAdd,
    canRemoveItem,
    getRemoveItemIndices(index: number) {
      if (removeTargetCount === null) {
        return [];
      }
      return getAdminV2ArrayRemovalIndices({
        itemCount: params.items.length,
        index,
        targetCount: removeTargetCount,
      });
    },
    addItems() {
      if (!canAdd) {
        return;
      }
      const additions = Array.from(
        { length: targetCount - params.items.length },
        params.createItem
      );
      params.onChange([...params.items, ...additions]);
    },
    updateItem(index: number, item: TItem) {
      params.onChange(
        updateAdminV2ArrayItem({ items: params.items, index, item })
      );
    },
    removeItem(index: number) {
      if (removeTargetCount === null || params.readOnly) {
        return;
      }
      params.onChange(
        removeAdminV2ArrayItemsToTargetCount({
          items: params.items,
          index,
          targetCount: removeTargetCount,
        })
      );
    },
    moveItem(index: number, direction: AdminV2ArrayDirection) {
      if (
        params.readOnly ||
        (direction === -1 && index === 0) ||
        (direction === 1 && index >= params.items.length - 1)
      ) {
        return;
      }
      params.onChange(
        moveAdminV2ArrayItem({
          items: params.items,
          index,
          direction,
        })
      );
    },
  };
}
